import { useState, useCallback, useRef } from "react";
import { useSendTransaction, useSignTypedData, useConfig } from "wagmi";
import { waitForTransactionReceipt } from "@wagmi/core";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { VersionedTransaction } from "@solana/web3.js";
import type { ISolana } from "@dynamic-labs/solana-core";
import type { EIP712Data, Route, Step, TxPayload, EvmTx, ChainType } from "~/api/types";
import { getViemChain } from "~/lib/chains";
import { useChains } from "~/hooks/useChains";
import { API_URL } from "~/lib/constants";

export type StepStatus =
  | "idle"
  | "pending"
  | "signing"
  | "submitting"
  | "polling"
  | "completed"
  | "failed";

export interface StepState {
  step: Step;
  status: StepStatus;
  txHash?: string;
  error?: string;
}

export interface ExecuteStepsResult {
  stepStates: StepState[];
  currentStepIndex: number;
  isExecuting: boolean;
  execute: (route: Route) => void;
  retry: () => void;
  reset: () => void;
}

function evmTxToWagmiParams(tx: EvmTx) {
  return {
    to: tx.to as `0x${string}`,
    data: tx.data as `0x${string}`,
    value: BigInt(tx.value),
    ...(tx.gas ? { gas: BigInt(tx.gas) } : {}),
  };
}

/** Placeholder addresses used for quote-only requests — never safe to execute */
const PLACEHOLDER_RECIPIENTS: Record<ChainType, string> = {
  evm: "0xd8da6bf26964af9d7eed9e03e53415d37aa96045",
  solana: "11111111111111111111111111111111",
  tron: "T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb",
};

const PLACEHOLDER_SET = new Set(Object.values(PLACEHOLDER_RECIPIENTS).map((a) => a.toLowerCase()));

// ERC20 approve(address,uint256) selector
const APPROVE_SELECTOR = "0x095ea7b3";

function buildZeroApprovalData(originalData: string): `0x${string}` {
  const spenderPart = originalData.slice(10, 74);
  return `${APPROVE_SELECTOR}${spenderPart}${"0".padStart(64, "0")}` as `0x${string}`;
}

function stripEip712Domain(types: EIP712Data["types"]) {
  return Object.fromEntries(Object.entries(types).filter(([name]) => name !== "EIP712Domain"));
}

function normalizeChainId(chainId: number | string | undefined): string | null {
  if (chainId == null) return null;
  if (typeof chainId === "number") return String(chainId);
  if (chainId.startsWith("0x")) {
    const parsed = Number.parseInt(chainId, 16);
    return Number.isNaN(parsed) ? null : String(parsed);
  }
  return chainId;
}

function isRequestedChainError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message.includes("Provider is not connected to the requested chain");
}

function getStepRequiredChainId(step: Step): number | null {
  switch (step.type) {
    case "APPROVAL":
    case "SWAP":
    case "WRAP":
    case "UNWRAP":
    case "CLAIM":
      return step.tx?.type === "evm" || step.type === "APPROVAL" ? Number(step.chainId) : null;
    case "ORDER_SUBMISSION":
      return Number(step.chainId);
    case "BRIDGE":
      return step.tx?.type === "evm" ? Number(step.fromChainId) : null;
    default:
      return null;
  }
}

export function useExecuteSteps(): ExecuteStepsResult {
  const config = useConfig();
  const { primaryWallet } = useDynamicContext();
  const { getChain } = useChains();
  const [stepStates, setStepStates] = useState<StepState[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [isExecuting, setIsExecuting] = useState(false);
  const routeRef = useRef<Route | null>(null);
  const abortRef = useRef(false);
  const trackedRef = useRef(false);

  const { sendTransactionAsync } = useSendTransaction();
  const { signTypedDataAsync } = useSignTypedData();

  const updateStep = useCallback((index: number, update: Partial<StepState>) => {
    setStepStates((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...update };
      return next;
    });
  }, []);

  const getChainName = useCallback(
    (chainId: number) => {
      return getChain(String(chainId))?.name ?? getViemChain(chainId)?.name ?? `chain ${chainId}`;
    },
    [getChain],
  );

  const createChainMismatchError = useCallback(
    (chainId: number) => {
      return new Error(
        `Wallet is not connected to ${getChainName(chainId)}. Switch networks and try again.`,
      );
    },
    [getChainName],
  );

  const ensureWalletOnChain = useCallback(
    async (chainId: number) => {
      if (!primaryWallet) return;
      const connectedChainId = normalizeChainId(await primaryWallet.getNetwork());
      if (connectedChainId && connectedChainId !== String(chainId)) {
        throw createChainMismatchError(chainId);
      }
    },
    [createChainMismatchError, primaryWallet],
  );

  const sendEvmTx = useCallback(
    async (tx: EvmTx, chainId?: number): Promise<`0x${string}`> => {
      const params = evmTxToWagmiParams(tx);
      if (chainId == null) {
        return sendTransactionAsync(params);
      }
      await ensureWalletOnChain(chainId);

      try {
        return await sendTransactionAsync({ ...params, chainId });
      } catch (error) {
        if (!isRequestedChainError(error)) {
          throw error;
        }

        await new Promise((resolve) => window.setTimeout(resolve, 400));
        await ensureWalletOnChain(chainId);

        try {
          return await sendTransactionAsync({ ...params, chainId });
        } catch (retryError) {
          if (isRequestedChainError(retryError)) {
            throw createChainMismatchError(chainId);
          }
          throw retryError;
        }
      }
    },
    [createChainMismatchError, ensureWalletOnChain, sendTransactionAsync],
  );

  const waitForEvmTx = useCallback(
    async (hash: `0x${string}`, chainId?: number) => {
      await waitForTransactionReceipt(config, {
        hash,
        ...(chainId ? { chainId } : {}),
      });
    },
    [config],
  );

  const sendTx = useCallback(
    async (
      tx: TxPayload,
      chainId?: number,
      onBroadcast?: (hash: string) => void,
    ): Promise<string> => {
      switch (tx.type) {
        case "evm": {
          const hash = await sendEvmTx(tx, chainId);
          onBroadcast?.(hash);
          await waitForEvmTx(hash, chainId);
          return hash;
        }

        case "solana": {
          if (!primaryWallet?.connector) {
            throw new Error("No Solana wallet connected. Connect a Solana wallet to execute.");
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Solana methods
          const signer = (await (primaryWallet.connector as any).getSigner()) as
            | ISolana
            | undefined;
          if (!signer) {
            throw new Error("Could not get Solana signer from wallet.");
          }
          const txBytes = Buffer.from(tx.serializedTransaction, "base64");
          const versionedTx = VersionedTransaction.deserialize(txBytes);
          const { signature } = await signer.signAndSendTransaction(versionedTx);
          onBroadcast?.(signature);
          return signature;
        }

        case "tron": {
          if (!primaryWallet?.connector) {
            throw new Error("No Tron wallet connected. Connect a Tron wallet to execute.");
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic Labs connector lacks typed Tron methods
          const tronWeb = (primaryWallet.connector as any).getWalletTronWeb?.();
          if (!tronWeb?.trx) {
            throw new Error("Could not get TronWeb instance from wallet.");
          }
          // Reconstruct the TronGrid transaction object for signing
          const txObject = {
            txID: tx.txID,
            raw_data_hex: tx.rawDataHex,
            ...(tx.rawData ? { raw_data: tx.rawData } : {}),
          };
          const signed = await tronWeb.trx.sign(txObject);
          const broadcast = await tronWeb.trx.sendRawTransaction(signed);
          if (!broadcast?.result) {
            throw new Error("Tron broadcast failed");
          }
          onBroadcast?.(broadcast.txid);
          return broadcast.txid;
        }
      }
    },
    [primaryWallet, sendEvmTx, waitForEvmTx],
  );

  /** Fire-and-forget POST /v1/track after the deposit tx broadcasts.
   *  Only sends identity fields — backend enriches from KV (trusted). */
  const trackOrder = useCallback(
    (route: Route, depositTx: string, step: Step) => {
      if (trackedRef.current) return;
      trackedRef.current = true;

      const sender = primaryWallet?.address;
      if (!sender) return;

      const lastStep = route.steps[route.steps.length - 1];
      const fromChainId = step.type === "BRIDGE" ? step.fromChainId : step.chainId;
      const toChainId =
        "toChainId" in lastStep
          ? (lastStep as { toChainId: string }).toChainId
          : "chainId" in lastStep
            ? (lastStep as { chainId: string }).chainId
            : fromChainId;

      fetch(`${API_URL}/v1/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adapter: route.summary.provider.name,
          routeId: route.routeId,
          depositTx,
          sender,
          fromChainId: String(fromChainId),
          toChainId: String(toChainId),
        }),
      }).catch(() => {}); // best-effort, never block execution
    },
    [primaryWallet],
  );

  const executeStep = useCallback(
    async (step: Step, index: number) => {
      if (abortRef.current) return;

      setCurrentStepIndex(index);
      updateStep(index, { status: "pending", txHash: undefined, error: undefined });

      try {
        switch (step.type) {
          case "APPROVAL": {
            if (step.tx.type !== "evm") {
              throw new Error("Non-EVM approval steps are not supported");
            }

            if (step.requiresResetApproval) {
              updateStep(index, { status: "signing", txHash: undefined });
              const zeroTx: EvmTx = {
                ...step.tx,
                data: buildZeroApprovalData(step.tx.data),
              };
              await sendTx(zeroTx, Number(step.chainId), (hash) => {
                updateStep(index, { status: "polling", txHash: hash });
              });
            }

            updateStep(index, { status: "signing", txHash: undefined });
            const hash = await sendTx(step.tx, Number(step.chainId), (broadcastHash) => {
              updateStep(index, { status: "polling", txHash: broadcastHash });
            });
            updateStep(index, { status: "completed", txHash: hash });
            break;
          }

          case "PERMIT": {
            updateStep(index, { status: "signing" });
            const signPayload = step.signPayload;
            if (signPayload.type === "eip712") {
              const { domain, types, primaryType, message } = signPayload.typedData;
              await signTypedDataAsync({
                domain: {
                  ...domain,
                  chainId: domain.chainId,
                  verifyingContract: domain.verifyingContract as `0x${string}`,
                },
                types: stripEip712Domain(types),
                primaryType,
                message,
              });
            } else {
              throw new Error("Solana permit signing not yet implemented");
            }
            updateStep(index, { status: "completed" });
            break;
          }

          case "SWAP":
          case "BRIDGE":
          case "WRAP":
          case "UNWRAP":
          case "CLAIM": {
            if (!step.tx) {
              updateStep(index, { status: "failed", error: "No transaction data" });
              return;
            }
            updateStep(index, { status: "signing", txHash: undefined });
            const chainId =
              step.type === "BRIDGE" ? Number(step.fromChainId) : Number(step.chainId);
            const hash = await sendTx(step.tx, chainId, (broadcastHash) => {
              updateStep(index, { status: "polling", txHash: broadcastHash });
              // Track the order as soon as the deposit tx broadcasts
              if (routeRef.current && (step.type === "SWAP" || step.type === "BRIDGE")) {
                trackOrder(routeRef.current, broadcastHash, step);
              }
            });
            updateStep(index, { status: "completed", txHash: hash });
            break;
          }

          case "ORDER_SUBMISSION": {
            await ensureWalletOnChain(Number(step.chainId));
            updateStep(index, { status: "signing" });
            const signPayload = step.signPayload;
            let signature: string;

            if (signPayload.type === "eip712") {
              const { domain, types, primaryType, message } = signPayload.typedData;
              signature = await signTypedDataAsync({
                domain: {
                  ...domain,
                  chainId: domain.chainId,
                  verifyingContract: domain.verifyingContract as `0x${string}`,
                },
                types: stripEip712Domain(types),
                primaryType,
                message,
              });
            } else {
              throw new Error("Solana order signing not yet implemented");
            }

            updateStep(index, { status: "submitting" });
            const res = await fetch(`${API_URL}/v1/order`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ step, signature }),
            });

            if (!res.ok) {
              const body = await res.text();
              throw new Error(`Order submission failed: ${body}`);
            }

            const submitResult = (await res.json()) as { providerOrderId: string };
            updateStep(index, { status: "completed", txHash: submitResult.providerOrderId });

            // Track intent-based orders so D1 has the full context for status enrichment
            if (routeRef.current) {
              trackOrder(routeRef.current, submitResult.providerOrderId, step);
            }
            break;
          }
        }
      } catch (err) {
        const requiredChainId = getStepRequiredChainId(step);
        const msg =
          requiredChainId != null && isRequestedChainError(err)
            ? createChainMismatchError(requiredChainId).message
            : err instanceof Error
              ? err.message
              : "Unknown error";
        updateStep(index, { status: "failed", error: msg });
        throw err;
      }
    },
    [createChainMismatchError, sendTx, signTypedDataAsync, trackOrder, updateStep],
  );

  const execute = useCallback(
    async (route: Route) => {
      // Safety: refuse to execute routes whose recipient is a known placeholder
      if (route.recipient && PLACEHOLDER_SET.has(route.recipient.toLowerCase())) {
        throw new Error(
          "Cannot execute: route was quoted with a placeholder recipient address. " +
            "Please enter a valid recipient and re-fetch the quote.",
        );
      }

      routeRef.current = route;
      abortRef.current = false;
      trackedRef.current = false;
      setIsExecuting(true);

      const states: StepState[] = route.steps.map((step) => ({
        step,
        status: "idle" as const,
      }));
      setStepStates(states);

      try {
        for (let i = 0; i < route.steps.length; i++) {
          if (abortRef.current) break;
          await executeStep(route.steps[i], i);
        }
      } catch {
        // Step failure already recorded in state
      } finally {
        setIsExecuting(false);
      }
    },
    [executeStep],
  );

  const retry = useCallback(async () => {
    const route = routeRef.current;
    if (!route || currentStepIndex < 0) return;

    if (route.expiresAt && new Date(route.expiresAt) < new Date()) {
      updateStep(currentStepIndex, {
        status: "failed",
        error: "Route has expired. Please get a new quote.",
      });
      return;
    }

    setIsExecuting(true);
    try {
      for (let i = currentStepIndex; i < route.steps.length; i++) {
        if (abortRef.current) break;
        await executeStep(route.steps[i], i);
      }
    } catch {
      // Step failure already recorded
    } finally {
      setIsExecuting(false);
    }
  }, [currentStepIndex, executeStep, updateStep]);

  const reset = useCallback(() => {
    abortRef.current = true;
    routeRef.current = null;
    setStepStates([]);
    setCurrentStepIndex(-1);
    setIsExecuting(false);
  }, []);

  return {
    stepStates,
    currentStepIndex,
    isExecuting,
    execute,
    retry,
    reset,
  };
}
